'use client';

import { PageLoader } from '@/components/shared';
import type React from 'react';
import { createContext, useContext, useState, useEffect, useCallback } from 'react';
export const locales = [{
  code: 'en',
  name: 'English'
}, {
  code: 'es',
  name: 'Español'
}, {
  code: 'fr',
  name: 'Français'
}, {
  code: 'de',
  name: 'Deutsch'
}, {
  code: 'ar',
  name: 'العربية'
}, {
  code: 'ko',
  name: '한국어'
}, {
  code: 'ja',
  name: '日本語'
}];
type Locale = (typeof locales)[number]['code'];
interface I18nContextType {
  locale: Locale;
  setLocale: (locale: Locale) => void;
  t: (key: string, params?: Record<string, string | number>) => string;
}
const I18nContext = createContext<I18nContextType | undefined>(undefined);
export function I18nProvider({
  children
}: {
  children: React.ReactNode;
}) {
  const [locale, setLocaleState] = useState<Locale | null>(null);
  const [translations, setTranslations] = useState<Record<string, any>>({});
  const [isLoading, setIsLoading] = useState(true);

  // Preload and cache all available translations when the app starts
  useEffect(() => {
    const preloadTranslations = async () => {
      // Only preload if we have an internet connection
      if (!navigator.onLine) return;
      const preloadPromises = locales.map(async localeObj => {
        const cacheKey = `translations_${localeObj.code}`;

        // Always fetch fresh translations on app access
        try {
          const response = await fetch(`/locales/${localeObj.code}.json`);
          if (response.ok) {
            const data = await response.json();
            localStorage.setItem(cacheKey, JSON.stringify(data));
            localStorage.setItem(`${cacheKey}_timestamp`, Date.now().toString());
          }
        } catch (error) {
          console.warn(`Failed to preload translations for ${localeObj.code}:`, error);
        }
      });
      await Promise.allSettled(preloadPromises);
    };

    // Clean up old cache entries to prevent localStorage overflow
    const cleanupOldCache = () => {
      try {
        const keys = Object.keys(localStorage);
        keys.forEach(key => {
          if (key.startsWith('translations_') && key.endsWith('_timestamp')) {
            const timestamp = localStorage.getItem(key);
            if (timestamp && Date.now() - parseInt(timestamp) > 30 * 24 * 60 * 60 * 1000) {
              // 30 days
              const baseKey = key.replace('_timestamp', '');
              localStorage.removeItem(key);
              localStorage.removeItem(baseKey);
            }
          }
        });
      } catch (error) {
        console.warn('Failed to cleanup translation cache:', error);
      }
    };
    cleanupOldCache();
    preloadTranslations();
  }, []);
  useEffect(() => {
    const searchParams = new URLSearchParams(window.location.search);
    const urlLang = searchParams.get('lang') as Locale | null;
    const savedLocale = localStorage.getItem('locale') as Locale | null;
    const validLang = (code: string | null): code is Locale => code !== null && locales.some(l => l.code === code);
    if (validLang(urlLang)) {
      setLocaleState(urlLang);
      localStorage.setItem('locale', urlLang);
      searchParams.delete('lang');
      window.history.replaceState({}, document.title, window.location.pathname + '?' + searchParams.toString());
    } else if (validLang(savedLocale)) {
      setLocaleState(savedLocale);
    } else {
      setLocaleState('en');
    }
  }, []);
  useEffect(() => {
    const loadTranslations = async () => {
      if (!locale) return;
      setIsLoading(true);

      // First, try to load from localStorage cache for instant display
      const cacheKey = `translations_${locale}`;
      const cachedTranslations = localStorage.getItem(cacheKey);
      if (cachedTranslations) {
        try {
          const parsedTranslations = JSON.parse(cachedTranslations);
          setTranslations(parsedTranslations);
          setIsLoading(false);
        } catch (parseError) {
          console.warn(`Failed to parse cached translations for ${locale}:`, parseError);
        }
      }

      // Always fetch fresh translations to update cache and state on every app access
      await fetchAndCacheTranslations(locale, cacheKey, !cachedTranslations, true);
    };
    const fetchAndCacheTranslations = async (targetLocale: string, cacheKey: string, updateLoadingState: boolean, alwaysUpdateState: boolean = false) => {
      try {
        const response = await fetch(`/locales/${targetLocale}.json`);
        if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
        const data = await response.json();

        // Cache the translations with timestamp
        try {
          localStorage.setItem(cacheKey, JSON.stringify(data));
          localStorage.setItem(`${cacheKey}_timestamp`, Date.now().toString());
        } catch (storageError) {
          console.warn('Failed to cache translations:', storageError);
        }

        // Update state if needed (either for initial load or to refresh with new keys)
        if (updateLoadingState || alwaysUpdateState) {
          setTranslations(data);
        }
      } catch (error) {
        console.error(`Could not load translations for locale: ${targetLocale}`, error);
        if (updateLoadingState) {
          // Try fallback to English if current locale is not English
          if (targetLocale !== 'en') {
            try {
              const fallbackCacheKey = 'translations_en';
              const cachedEnglish = localStorage.getItem(fallbackCacheKey);
              if (cachedEnglish) {
                const parsedEnglish = JSON.parse(cachedEnglish);
                setTranslations(parsedEnglish);
                return;
              }
              const fallbackResponse = await fetch(`/locales/en.json`);
              if (!fallbackResponse.ok) throw new Error(`HTTP error! status: ${fallbackResponse.status} for fallback`);
              const fallbackData = await fallbackResponse.json();

              // Cache English fallback with timestamp
              try {
                localStorage.setItem(fallbackCacheKey, JSON.stringify(fallbackData));
                localStorage.setItem(`${fallbackCacheKey}_timestamp`, Date.now().toString());
              } catch (storageError) {
                console.warn('Failed to cache English fallback:', storageError);
              }
              setTranslations(fallbackData);
            } catch (fallbackError) {
              console.error(`Could not load fallback translations for locale: en`, fallbackError);

              // Last resort: check if we have any cached English translations
              const lastResortCache = localStorage.getItem('translations_en');
              if (lastResortCache) {
                try {
                  const parsedLastResort = JSON.parse(lastResortCache);
                  setTranslations(parsedLastResort);
                  return;
                } catch (parseError) {
                  console.error('Failed to parse last resort English cache:', parseError);
                }
              }
              setTranslations({});
            }
          } else {
            // Current locale is English, check cache one more time
            const englishCache = localStorage.getItem('translations_en');
            if (englishCache) {
              try {
                const parsedEnglish = JSON.parse(englishCache);
                setTranslations(parsedEnglish);
                return;
              } catch (parseError) {
                console.error('Failed to parse cached English translations:', parseError);
              }
            }
            setTranslations({});
          }
        }
      } finally {
        if (updateLoadingState) {
          setIsLoading(false);
        }
      }
    };
    loadTranslations();
  }, [locale]);
  const setLocale = (newLocale: Locale) => {
    setLocaleState(newLocale);
    localStorage.setItem('locale', newLocale);
    document.documentElement.lang = newLocale;
  };
  const t = useCallback((key: string, params?: Record<string, string | number>): string => {
    const keys = key.split('.');
    let result = translations;
    for (const k of keys) {
      result = result?.[k];
      if (result === undefined) return key;
    }
    if (typeof result === 'string' && params) {
      return Object.entries(params).reduce<string>((acc, [paramKey, paramValue]) => acc.replace(`{${paramKey}}`, String(paramValue)), result);
    }
    return typeof result === 'string' ? result : key;
  }, [translations]);
  const value = {
    locale: locale || 'en',
    setLocale,
    t
  };
  if (isLoading || !locale) {
    return <PageLoader className="h-screen" />;
  }
  return <I18nContext.Provider value={value} data-sentry-element="I18nContext.Provider" data-sentry-component="I18nProvider" data-sentry-source-file="index.tsx">{children}</I18nContext.Provider>;
}
export function useI18n() {
  const context = useContext(I18nContext);
  if (context === undefined) {
    throw new Error('useI18n must be used within an I18nProvider');
  }
  return context;
}